import { FC } from 'react';
import VersionsChip from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/VersionChip';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { Typography, styled } from '@mui/material';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import { rootElementId } from '@/constants/constants';
import CalendarButtonInput from './CalendarButtonInput';
import { FocusTheme } from '@/components/Focus/configs/theme';
import RightsRestriction from '../../RightsRestriction';
import RoundIconButton from '@/components/Focus/common/RoundIconButton';
import { StyledLeftBorder } from './ReviewScheduleAccordionHeader';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';
import LaunchTypeChip from './AddIncrementalLaunch/LaunchTypeChip';
import { LaunchType } from './IncrementalLaunchDetailsRow';
import IncrementalLaunchAvailChip from './IncrementalLaunchAvailChip';

export type Option = {
  label: string;
  value: string;
};

// Props shared by both first-time and incremental rows.
type BaseRowProps = {
  occurrenceStartDate: string;
  date: string;
  time: string;
  title: string;
  subTitle: string;
  versionChipLabel: string;
  launchType: LaunchType;
  parentEntityTypeChipLabel?: string;
  onDelete?: () => void;
};

// Avail data for a first-time row.
type FirstTimeAvailData = {
  territoryItems: Option[];
  dubItems: Option[];
  subItems: Option[];
};

// Avail data for an incremental row.
type IncrementalAvailData = {
  territoryAddedValues: string[];
  territoryRemovedValues: string[];
  dubAddedValues: string[];
  dubRemovedValues: string[];
  subAddedValues: string[];
  subRemovedValues: string[];
  allLanguagesMap?: Record<string, { label: string }>;
};

// Props for the first-time launch row.
type FirstTimeRowProps = BaseRowProps & {
  launchType: 'first-time';
  rightsRestrictionId?: number;
  onChangeDate?: (iso: string) => void;
  availData: FirstTimeAvailData;
};

// Props for the incremental launch row.
type IncrementalRowProps = BaseRowProps & {
  launchType: 'incremental';
  rightsRestrictionId: number;
  launchId: string;
  availData: IncrementalAvailData;
};

export type ReviewScheduleListRowProps = FirstTimeRowProps | IncrementalRowProps;

const ReviewScheduleListRow: FC<ReviewScheduleListRowProps> = (props) => {
  const theme = useFocusTheme();

  const { occurrenceStartDate, date, time, title, subTitle, versionChipLabel, parentEntityTypeChipLabel, onDelete, launchType } = props;

  const isIncrementalRow = launchType === 'incremental';

  const firstTimeRowProps: FirstTimeRowProps | null = launchType === 'first-time' ? props : null;
  const incrementalRowProps: IncrementalRowProps | null = launchType === 'incremental' ? props : null;

  const firstTimeAvailData = firstTimeRowProps?.availData;
  const incrementalAvailData = incrementalRowProps?.availData;

  return (
    <StyledRow isIndented={isIncrementalRow}>
      <StyledLeftRow>
        <StyledDateDisplay>
          {isIncrementalRow ? (
            <CalendarButtonInput date={date} time={time} />
          ) : (
            <Datepicker
              bare
              value={occurrenceStartDate}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Time"
              showIcon={false}
              portalId={rootElementId}
              popperPlacement="bottom-start"
              popperProps={{ strategy: 'fixed' }}
              onChange={(d) => {
                if (!d || !firstTimeRowProps?.onChangeDate) return;
                firstTimeRowProps.onChangeDate(d.toISOString());
              }}
              customInput={<CalendarButtonInput date={date} time={time} />}
            />
          )}
        </StyledDateDisplay>
        <StyledLaunchTypeChipWrapper>
          <LaunchTypeChip type={launchType} />
        </StyledLaunchTypeChipWrapper>
        <StyledEpisodeInfo>
          <StyledLeftBorder accent={theme.palette.success.main}>
            <StyledEpisodeRow>
              <StyledVersionText>{title}</StyledVersionText>
              <VersionsChip versionName={versionChipLabel} size="small" />
              {!!parentEntityTypeChipLabel && <VersionsChip versionName={parentEntityTypeChipLabel} size="small" />}
            </StyledEpisodeRow>
            <StyledEpisodeName>{subTitle}</StyledEpisodeName>
          </StyledLeftBorder>
        </StyledEpisodeInfo>
      </StyledLeftRow>
      <StyledRightRow>
        <StyledRightsRestrictionSlot>
          {firstTimeRowProps && typeof firstTimeRowProps.rightsRestrictionId === 'number' && (
            <RightsRestriction id={firstTimeRowProps.rightsRestrictionId} />
          )}
          {incrementalRowProps && <RightsRestriction id={incrementalRowProps.rightsRestrictionId} launchId={incrementalRowProps.launchId} />}
        </StyledRightsRestrictionSlot>
        <StyledAvailChipRow>
          {incrementalAvailData ? (
            <IncrementalLaunchAvailChip
              icon={<PolarisIcon icon="map" color="white" />}
              label="Territories"
              launchType="incremental"
              type="territories"
              addedValues={incrementalAvailData.territoryAddedValues}
              removedValues={incrementalAvailData.territoryRemovedValues}
              allLanguagesMap={incrementalAvailData.allLanguagesMap}
            />
          ) : (
            <IncrementalLaunchAvailChip
              icon={<PolarisIcon icon="map" color="white" />}
              label="Territories"
              launchType="first-time"
              items={firstTimeAvailData?.territoryItems ?? []}
            />
          )}
          {incrementalAvailData ? (
            <IncrementalLaunchAvailChip
              icon={<PolarisIcon icon="music" color="white" />}
              label="Audio"
              launchType="incremental"
              type="audio"
              addedValues={incrementalAvailData.dubAddedValues}
              removedValues={incrementalAvailData.dubRemovedValues}
              allLanguagesMap={incrementalAvailData.allLanguagesMap}
            />
          ) : (
            <IncrementalLaunchAvailChip
              icon={<PolarisIcon icon="music" color="white" />}
              label="Audio"
              launchType="first-time"
              items={firstTimeAvailData?.dubItems ?? []}
            />
          )}
          {incrementalAvailData ? (
            <IncrementalLaunchAvailChip
              icon={<PolarisIcon icon="subtitles" color="white" />}
              label="Timed Text"
              launchType="incremental"
              type="timedtext"
              addedValues={incrementalAvailData.subAddedValues}
              removedValues={incrementalAvailData.subRemovedValues}
              allLanguagesMap={incrementalAvailData.allLanguagesMap}
            />
          ) : (
            <IncrementalLaunchAvailChip
              icon={<PolarisIcon icon="subtitles" color="white" />}
              label="Timed Text"
              launchType="first-time"
              items={firstTimeAvailData?.subItems ?? []}
            />
          )}
        </StyledAvailChipRow>
        <StyledActionArea>
          <StyledActionSlot>
            {!!onDelete && (
              <RoundIconButton
                icon="delete_outline"
                aria-label="Delete scheduling"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.();
                }}
              />
            )}
          </StyledActionSlot>
        </StyledActionArea>
      </StyledRightRow>
    </StyledRow>
  );
};

export default ReviewScheduleListRow;

const StyledRow = styled('div')<{ isIndented: boolean }>`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const StyledLeftRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
  flex: 1;
`;

const StyledLaunchTypeChipWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing(4)};
`;

const StyledRightRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 620px;
  min-width: 620px;
  flex-shrink: 0;
`;

const StyledRightsRestrictionSlot = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledActionArea = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 40px;
`;

const StyledActionSlot = styled('div')`
  width: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledDateDisplay = styled('div')`
  width: 210px;
  min-width: 210px;
  max-width: 210px;
  flex: 0 0 210px;

  .react-datepicker-wrapper {
    display: inline-block;
    width: 210px;
    min-width: 210px;
    max-width: 210px;
    flex: 0 0 210px;
  }

  .react-datepicker__input-container {
    width: 210px;
    min-width: 210px;
    max-width: 210px;
  }
`;

const StyledVersionText = styled(Typography)`
  font-size: 16px;
  line-height: 1.2;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
`;

const StyledEpisodeInfo = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledEpisodeRow = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const StyledEpisodeName = styled(Typography)`
  font-size: 16px;
`;

const StyledAvailChipRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-shrink: 0;
`;
